import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { InlineNotification, Link, ModalWindow, RadioGroup, RadioGroupOption, Spinner, Text, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { useApiClient } from '../../features/api_client/hooks/use_api_client.hook';
import { ApiClientService } from '../../features/api_client/types/api_client.constants';
import { AuthEndpoint } from '../../features/auth/types/auth.constants';
import { maskEmail, maskPhone } from '../../features/auth/utils/mfa.utils';
import { useCooldown } from '../../hooks';
import { OtpInput } from '../otp_input';
const RESEND_COOLDOWN_SECONDS = 30;
const STEP_UP_REQUEST_CONFIG = {
    no401Handling: true,
    _skipStepUpHandling: true,
};
export const StepUpMfaModal = ({ isOpen, onClose, onSuccess, prefetchedMethods }) => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const iamClient = useApiClient(ApiClientService.IAM);
    const [step, setStep] = useState('loading');
    const [methods, setMethods] = useState([]);
    const [selectedMethod, setSelectedMethod] = useState(undefined);
    const [code, setCode] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [cooldown, setCooldown] = useCooldown();
    const [error, setError] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const hasAutoSelected = useRef(false);
    const hasInitializedRef = useRef(false);
    const sendChallenge = useCallback(async (method) => {
        const targetMethod = method || selectedMethod;
        if (!targetMethod || isSending) {
            return;
        }
        setIsSending(true);
        setError(null);
        try {
            await iamClient.post(AuthEndpoint.MFA_CHALLENGE, { mfaMethodId: targetMethod.id }, STEP_UP_REQUEST_CONFIG);
            setCooldown(RESEND_COOLDOWN_SECONDS);
        }
        catch {
            setError(translate('Failed to send verification code. Please try again.'));
            addNotification({
                title: translate('Error'),
                subtitle: translate('Failed to send verification code. Please try again.'),
                type: 'error',
            });
        }
        finally {
            setIsSending(false);
        }
    }, [addNotification, iamClient, isSending, selectedMethod, translate]);
    const initializeModalFromMethods = useCallback(async (availableMethods) => {
        if (!availableMethods.length) {
            setErrorMessage(translate('No MFA methods enrolled. Please set up MFA in your profile first.'));
            setStep('error');
            return;
        }
        setMethods(availableMethods);
        if (availableMethods.length === 1) {
            hasAutoSelected.current = true;
            setSelectedMethod(availableMethods[0]);
            if (availableMethods[0].type === 'totp') {
                setStep('code_verification');
            }
            else {
                setStep('code_verification');
                void sendChallenge(availableMethods[0]);
            }
        }
        else {
            setSelectedMethod(availableMethods[0]);
            setStep('method_selection');
        }
    }, [sendChallenge, translate]);
    // Initialize methods when modal opens
    useEffect(() => {
        if (!isOpen) {
            hasInitializedRef.current = false;
            return;
        }
        if (hasInitializedRef.current) {
            return;
        }
        hasInitializedRef.current = true;
        let isCancelled = false;
        const initialize = async () => {
            setStep('loading');
            setMethods([]);
            setSelectedMethod(undefined);
            setCode('');
            setCooldown(0);
            setError(null);
            setErrorMessage('');
            hasAutoSelected.current = false;
            try {
                const methodsToUse = prefetchedMethods?.length
                    ? prefetchedMethods
                    : (await iamClient.get('/v1/mfa', STEP_UP_REQUEST_CONFIG)).data.data.map((method) => ({
                        id: method.id,
                        type: method.type,
                        identifier: method.identifier,
                    }));
                if (isCancelled) {
                    return;
                }
                await initializeModalFromMethods(methodsToUse);
            }
            catch {
                if (isCancelled) {
                    return;
                }
                setErrorMessage(translate('Failed to load verification methods.'));
                setStep('error');
            }
        };
        if (isOpen) {
            void initialize();
        }
        return () => {
            isCancelled = true;
        };
    }, [isOpen, prefetchedMethods, iamClient, initializeModalFromMethods, translate]);
    const handleNextFromMethodSelection = async () => {
        if (!selectedMethod) {
            return;
        }
        if (selectedMethod.type === 'totp') {
            setStep('code_verification');
        }
        else {
            setStep('code_verification');
            void sendChallenge();
        }
    };
    const handleResend = useCallback(async () => {
        setCode('');
        setError(null);
        await sendChallenge();
    }, [sendChallenge]);
    const handleVerify = async () => {
        if (!code.trim()) {
            return;
        }
        setIsSubmitting(true);
        setError(null);
        try {
            await iamClient.post(AuthEndpoint.MFA_CHALLENGE_VERIFY, {
                code: code.trim(),
            }, STEP_UP_REQUEST_CONFIG);
            onSuccess();
        }
        catch {
            setError(translate('Incorrect code entered'));
            setCode('');
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleBack = () => {
        setError(null);
        setCode('');
        if (methods.length > 1) {
            setStep('method_selection');
        }
        else {
            onClose();
        }
    };
    const getMethodLabel = (method) => {
        switch (method.type) {
            case 'email':
                return method.identifier ? `${translate('Email to')} ${maskEmail(method.identifier)}` : translate('Email');
            case 'sms':
                return method.identifier
                    ? `${translate('Text to')} ${maskPhone(method.identifier)}`
                    : translate('Text message (SMS)');
            case 'totp':
                return translate('Authenticator app');
            default:
                return method.type;
        }
    };
    const cancelButton = { label: translate('Cancel'), onClick: onClose };
    const renderLoading = () => _jsx(Spinner, { wrapper: 'full' });
    const renderError = () => (_jsx("div", { className: 'flex flex-col gap-4', children: _jsx(InlineNotification, { type: 'error', title: errorMessage }) }));
    const renderMethodSelection = () => (_jsxs("div", { className: 'flex flex-col gap-6', children: [_jsx(Text, { size: 'sm', className: 'text-secondary', children: translate('For security, please verify your identity before proceeding.') }), _jsx(Text, { size: 'sm', className: 'text-secondary', children: translate("Select how you'd like to receive your verification code.") }), _jsx(RadioGroup, { value: selectedMethod, onChange: (value) => setSelectedMethod(value), children: methods.map((method) => (_jsx(RadioGroupOption, { option: {
                        value: method,
                        label: getMethodLabel(method),
                    } }, method.id))) })] }));
    const renderCodeVerification = () => (_jsxs("div", { className: 'flex flex-col gap-6', children: [methods.length > 1 && (_jsxs("button", { type: 'button', onClick: handleBack, className: 'flex items-center gap-1 self-start text-sm text-secondary hover:text-primary', children: [_jsx("span", { children: "\u2190" }), " ", translate('Back')] })), error && _jsx(InlineNotification, { type: 'error', title: error }), _jsx(Text, { size: 'sm', className: 'text-secondary', children: selectedMethod?.type === 'totp'
                    ? translate('Please enter the one-time code displayed in your authenticator app')
                    : `${translate('A verification code has been sent to')} ${selectedMethod?.identifier || ''}.` }), _jsx(OtpInput, { value: code, onChange: setCode, hasError: !!error }), selectedMethod?.type !== 'totp' && (_jsxs("div", { className: 'flex items-center justify-center gap-2', children: [_jsx(Text, { size: 'sm', className: 'text-secondary', children: translate("Haven't received a code?") }), _jsx(Link, { to: '#', onClick: handleResend, disabled: cooldown > 0 || isSending, children: cooldown > 0 ? `${translate('Resend new')} (${cooldown}s)` : translate('Resend new') })] }))] }));
    const getFooterButton = () => {
        if (step === 'method_selection') {
            return {
                label: translate('Next'),
                onClick: handleNextFromMethodSelection,
                disabled: !selectedMethod || isSending,
            };
        }
        if (step === 'code_verification') {
            return {
                label: translate('Verify'),
                onClick: handleVerify,
                disabled: code.length < 6 || isSubmitting,
            };
        }
        return undefined;
    };
    return (_jsxs(ModalWindow, { title: translate('Identity verification'), isOpen: isOpen, closeWindow: onClose, width: 'sm', footerSecondaryActionButtons: [cancelButton], footerPrimaryActionButton: getFooterButton(), children: [step === 'loading' && renderLoading(), step === 'error' && renderError(), step === 'method_selection' && renderMethodSelection(), step === 'code_verification' && renderCodeVerification()] }));
};
